import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, ChevronDown } from 'lucide-react';
import { navLinks, servicesDropdown } from '../../data/mockData';

const Logo = () => (
  <Link to="/" className="flex items-center gap-0 group" aria-label="WebNaTak - strona główna">
    <span className="flex items-center justify-center w-8 h-8 rounded-full border-2 border-white/80 text-white font-heading font-bold text-lg group-hover:border-white transition-colors">
      W
    </span>
    <span
      className="text-white font-paragraph text-lg tracking-tight"
      style={{ marginLeft: '1px' }}
    >
      ebnatak
    </span>
  </Link>
);

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();
  const servicesRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsMobileMenuOpen(false);
    setIsServicesOpen(false);
    setIsMobileServicesOpen(false);
  }, [location.pathname]);

  // close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setIsServicesOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled ? 'bg-[#2d4059]/95 backdrop-blur-sm shadow-lg' : 'bg-[#2d4059]/40 backdrop-blur-[2px]'
      }`}
    >
      <nav
        className="max-w-[120rem] mx-auto px-6 h-16 flex items-center justify-between"
        aria-label="Główna nawigacja"
      >
        <Logo />

        {/* Desktop Nav */}
        <div className="hidden md:flex items-center gap-7">
          {navLinks.map((link) => (
            <Link
              key={link.path}
              to={link.path}
              className={`text-sm font-paragraph transition-colors ${
                location.pathname === link.path
                  ? 'text-white font-semibold'
                  : 'text-white/80 hover:text-white'
              }`}
            >
              {link.name}
            </Link>
          ))}

          {/* Services dropdown */}
          <div className="relative" ref={servicesRef}>
            <button
              type="button"
              onClick={() => setIsServicesOpen((v) => !v)}
              className="flex items-center gap-1 text-sm font-paragraph text-white/80 hover:text-white transition-colors"
              aria-expanded={isServicesOpen}
              aria-haspopup="true"
            >
              Usługi
              <ChevronDown
                size={14}
                className={`transition-transform ${isServicesOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isServicesOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-xl py-2 border border-[#2c3e50]/10">
                {servicesDropdown.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="block px-4 py-2 text-sm font-paragraph text-[#2c3e50] hover:bg-[#edf1f5] hover:text-[#e74c3c] transition-colors"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-3">
          <Link
            to="/login"
            className="px-4 py-2 text-white/80 hover:text-white text-sm font-paragraph transition-colors"
          >
            Logowanie
          </Link>
          <Link
            to="/zamow"
            className="px-5 py-2 bg-[#e74c3c] hover:bg-[#d44332] text-white text-sm font-semibold rounded-md transition-colors"
          >
            Zamów stronę
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="md:hidden text-white p-2"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label="Otwórz menu"
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      </nav>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden bg-[#2d4059]/98 backdrop-blur-sm border-t border-white/10">
          <div className="px-6 py-4 flex flex-col gap-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                className={`text-sm font-paragraph py-2 ${
                  location.pathname === link.path
                    ? 'text-white font-semibold'
                    : 'text-white/80'
                }`}
              >
                {link.name}
              </Link>
            ))}

            <button
              type="button"
              onClick={() => setIsMobileServicesOpen((v) => !v)}
              className="flex items-center justify-between text-sm font-paragraph py-2 text-white/80"
              aria-expanded={isMobileServicesOpen}
            >
              Usługi
              <ChevronDown
                size={14}
                className={`transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`}
              />
            </button>
            {isMobileServicesOpen && (
              <div className="pl-4 flex flex-col gap-2 mb-2">
                {servicesDropdown.map((item) => (
                  <Link
                    key={item.path}
                    to={item.path}
                    className="text-xs font-paragraph text-white/70 py-1"
                  >
                    {item.name}
                  </Link>
                ))}
              </div>
            )}

            <Link
              to="/login"
              className="mt-2 text-sm font-paragraph py-2 text-white/80 text-center"
            >
              Logowanie
            </Link>
            <Link
              to="/zamow"
              className="px-5 py-2 bg-[#e74c3c] text-white text-sm font-semibold rounded-md text-center"
            >
              Zamów stronę
            </Link>
          </div>
        </div>
      )}
    </header>
  );
}
